<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { disabledClass } from '@tundr/theme';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import type { FieldMeta } from 'vee-validate';
  import Checkbox from '../../Checkbox/Checkbox.vue';
  import { LoaderContainer } from '../../../Loader';
  import {
    multiSelectWrapper,
    checkableOptionCheckboxClass,
    checkableOptionClass,
    checkableOptionDisabledMarkerClass,
    checkableOptionLabelClass,
    paginationLoaderClass,
  } from './MultiSelect.css';
  import type { MultiSelectItem, MultiSelectProps } from './MultiSelect.types';
  import Select from '../Select/Select.vue';

  const { t } = useLocale('ui-kit');

  const props = withDefaults(defineProps<MultiSelectProps>(), {
    searchable: true,
    showLabels: false,
    showNoOptions: true,
    resettable: false,
    showNoResults: true,
    allowEmpty: false,
    taggable: false,
    disabled: false,
    meta: undefined,
    internalSearch: true,
    valueKey: 'value',
    labelKey: 'label',
  });

  const emit = defineEmits<{
    blur: [ev?: Event, shouldValidate?: boolean];
    addTag: [value: string];
    search: [value: string];
    reset: [];
    'update:modelValue': [value: MultiSelectItem[]];
    select: [value: MultiSelectItem[]];
  }>();

  const multiselectRef = ref<InstanceType<typeof Select>>();
  const isLoadingMore = ref(false);
  let scrollElement: HTMLElement | null = null;

  const handleScroll = (event: Event) => {
    if (!props.canLoadMore || isLoadingMore.value || props.paginationLoading)
      return;
    if (!props.canLoadMore()) return;

    const target = event.target as HTMLElement;
    const threshold = 10;

    if (
      target.scrollHeight - target.scrollTop - target.clientHeight <
      threshold
    ) {
      handleLoadMore();
    }
  };

  const handleLoadMore = async () => {
    if (!props.onLoadMore || isLoadingMore.value) return;

    try {
      isLoadingMore.value = true;
      const result = props.onLoadMore();
      if (result && typeof result === 'object' && 'then' in result) {
        await result;
      }
    } catch (error) {
      console.error('Failed to load more items:', error);
    } finally {
      isLoadingMore.value = false;
    }
  };

  const detachScrollListener = () => {
    if (scrollElement) {
      scrollElement.removeEventListener('scroll', handleScroll);
      scrollElement = null;
    }
  };

  onMounted(() => {
    if (!props.canLoadMore) return;

    const observer = new MutationObserver(() => {
      const wrapper = multiselectRef.value?.$el?.querySelector(
        '.multiselect__content-wrapper',
      ) as HTMLElement | null;

      if (wrapper && !scrollElement) {
        // Dropdown just opened
        scrollElement = wrapper;
        scrollElement.addEventListener('scroll', handleScroll, {
          passive: true,
        });
      } else if (!wrapper && scrollElement) {
        // Dropdown closed
        detachScrollListener();
      }
    });

    // Observe the entire multiselect element for changes
    if (multiselectRef.value?.$el) {
      observer.observe(multiselectRef.value.$el, {
        childList: true,
        subtree: true,
      });
    }

    onUnmounted(() => {
      observer.disconnect();
    });
  });

  onUnmounted(() => {
    detachScrollListener();
  });

  // Computed property for selected count to ensure reactivity
  const selectedCount = computed(() => {
    return props.modelValue?.length || 0;
  });

  const selectProps = computed(() => ({
    ...props,
    multiple: true,
    closeOnSelect: false,
    clearOnSelect: false,
    trackBy: props.valueKey,
    optionLabel: props.labelKey,
    internalSearch: props.searchCallback ? false : props.internalSearch,
    meta: props.meta as unknown as FieldMeta<string> | undefined,
    optionClass: (option: MultiSelectItem) =>
      option.disabled ? 'option-disabled' : '',
  }));

  const handleBlur = (ev?: Event, shouldValidate?: boolean) => {
    emit('blur', ev, shouldValidate);
  };

  const handleTag = (value: string) => {
    emit('addTag', value);
  };

  const handleSearch = (value: unknown) => {
    const searchQuery = typeof value === 'string' ? value : '';

    if (props.searchCallback) {
      props.searchCallback(searchQuery);
    }

    emit('search', searchQuery);
  };

  const handleReset = () => {
    emit('reset');
  };

  const isOptionSelected = (option: MultiSelectItem): boolean => {
    if (!props.modelValue || !Array.isArray(props.modelValue)) {
      return false;
    }

    const optionValue = option[props.valueKey];

    return props.modelValue.some((selected) => {
      // Handle both cases: selected can be an object or a primitive value (id)
      if (typeof selected === 'object' && selected !== null) {
        return selected[props.valueKey] === optionValue;
      } else {
        // If selected is a primitive (like an ID string), compare directly
        return selected === optionValue;
      }
    });
  };

  const getOptionLabel = (option: MultiSelectItem): string => {
    return String(option[props.labelKey]);
  };

  const getOptionValue = (option: MultiSelectItem): string | number => {
    return option[props.valueKey] as string | number;
  };

  const handleOptionClick = async (option: MultiSelectItem) => {
    if (props.disabled || option.disabled) return;

    const isSelected = isOptionSelected(option);
    let newValue: MultiSelectItem[];
    const currentValue = props.modelValue || [];
    const optionValue = option[props.valueKey];

    if (isSelected) {
      newValue = currentValue.filter((selected) => {
        if (typeof selected === 'object' && selected !== null) {
          return selected[props.valueKey] !== optionValue;
        } else {
          return selected !== optionValue;
        }
      }) as MultiSelectItem[];
    } else {
      newValue = [...currentValue, optionValue] as MultiSelectItem[];
    }

    emit('update:modelValue', newValue);
    emit('select', newValue);
  };

  defineOptions({
    inheritAttrs: false,
  });
</script>

<template>
  <Select
    ref="multiselectRef"
    v-bind="selectProps"
    :class="`multi-select ${multiSelectWrapper}`"
    @blur="handleBlur"
    @add-tag="handleTag"
    @search="handleSearch"
    @reset="handleReset"
  >
    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope" :key="slot">
      <slot
        v-if="slot !== 'option' && slot !== 'selection'"
        :name="slot"
        v-bind="scope"
      />
    </template>

    <template #selection="{ values, isOpen }">
      <slot
        v-if="$slots.selection"
        name="selection"
        v-bind="{ values, isOpen }"
      />
      <template v-else>
        <span
          class="multiselect__single"
          v-if="selectedCount > 0"
          v-show="!isOpen"
        >
          {{ t('checkable_select.optionsSelected', { count: selectedCount }) }}
        </span>
      </template>
    </template>

    <template #option="{ option }">
      <!-- biome-ignore lint/a11y/useKeyWithClickEvents: vue-multiselect handles arrow keys and Enter for option selection -->
      <li
        :class="[checkableOptionClass, { [disabledClass]: option.disabled }]"
        @click.stop="handleOptionClick(option)"
        :id="`listbox-${getOptionValue(option)}`"
      >
        <div
          v-if="option.disabled"
          :class="[
            checkableOptionDisabledMarkerClass,
            { [disabledClass]: option.disabled },
          ]"
        ></div>

        <div :class="checkableOptionCheckboxClass">
          <Checkbox
            :name="`multi-select-${getOptionValue(option)}`"
            :model-value="isOptionSelected(option)"
            :disabled="disabled || option.disabled"
          />
        </div>
        <span :class="checkableOptionLabelClass">
          {{ getOptionLabel(option) }}
        </span>
      </li>
    </template>

    <template #afterList v-if="canLoadMore">
      <LoaderContainer
        v-if="isLoadingMore || paginationLoading"
        :class="paginationLoaderClass"
      />
    </template>
  </Select>
</template>
